import { motion } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { CopilotCompactBreakpoint } from 'sprites-types/api/v2/copilot'
import { cn } from '../../lib/utils'

interface CompactBreakpointMessageProps {
  message: CopilotCompactBreakpoint
}

export function CompactBreakpointMessage({ message }: CompactBreakpointMessageProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const originalMessageCount = message.originalMessageCount || 0
  const compactedCount = message.compactedInteractionIds?.length || 0
  const displayCount = originalMessageCount > 0 ? originalMessageCount : compactedCount
  const countLabel = originalMessageCount > 0 ? 'messages' : 'interactions'

  return (
    <div className="my-3">
      {!isExpanded ? (
        // Collapsed view: Simple divider with badge
        <div className="flex items-center gap-2 text-xs text-neutral-400">
          <div className="h-px flex-1 bg-neutral-200" />
          <button
            onClick={() => setIsExpanded(true)}
            className="group flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 py-1.5 font-medium text-neutral-500 transition-colors hover:bg-neutral-200"
          >
            <span>
              {displayCount} {countLabel} compacted
            </span>
            <ChevronDown className="h-3 w-3 transition-transform group-hover:translate-y-0.5" />
          </button>
          <div className="h-px flex-1 bg-neutral-200" />
        </div>
      ) : (
        // Expanded view: Glass card with summary
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className={cn(
            'overflow-hidden rounded-2xl border border-gray-200/50 bg-white/70 shadow-sm backdrop-blur-[8px]'
          )}
        >
          <button
            onClick={() => setIsExpanded(false)}
            className="flex w-full items-center justify-between border-b border-gray-200/50 px-4 py-2.5 text-left transition-colors hover:bg-white/50"
          >
            <span className="text-xs font-medium text-neutral-500">
              {displayCount} {countLabel} compacted
            </span>
            <motion.div animate={{ rotate: 180 }} transition={{ duration: 0.2, ease: 'easeInOut' }}>
              <ChevronDown className="h-3 w-3 text-neutral-400" />
            </motion.div>
          </button>

          <div className="px-4 py-3">
            <div className="whitespace-pre-wrap text-sm text-neutral-600">{message.summary}</div>
          </div>
        </motion.div>
      )}
    </div>
  )
}
